import React, { useRef, useState } from 'react';
import { DropzoneOptions, FileError } from 'react-dropzone';
import { ModalHeader } from '../../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../../../../CommonComponents/OPL/Modal/Modal';
import { Dropzone } from '../../../../../../../CommonComponents/OPL/Dropzone/Dropzone';
import { MAX_FILE_SIZE } from '../../AutoAccountsAgreements.meta';
import { UploadAgreement } from '../UploadAgreement/UploadAgreement';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { useAddAgreementsMutation } from '../../api/useAddAgreementsMutation';
import { Alert } from '../../../../../../../CommonComponents/OPL/Alert/Alert';
import { AgreementDTO } from '../../../../api/useFirmSettingsQuery';

const getMessageFromErrors = (errors: FileError[]): string => {
  const error = errors[0];

  if (error.code === 'file-invalid-type') {
    return 'Only files with .pdf extensions are allowed.';
  }

  if (error.code === 'file-too-large') {
    return `The maximum file size is ${MAX_FILE_SIZE} MB.`;
  }

  return 'Invalid file';
};

interface Props {
  isFirm?: boolean;
  agreements: AgreementDTO[];
  onAddAgreements(agreements: AgreementDTO[]): void;
  onClose(): void;
}

export const UploadAgreementsModal: React.FC<Props> = ({
  isFirm,
  agreements,
  onAddAgreements,
  onClose,
}) => {
  const uploadedAgreements = useRef<File[]>([]);

  const [uploadingAgreements, setUploadingAgreements] = useState<File[]>([]);
  const [invalidAgreements, setInvalidAgreements] = useState<{ file: File; error: string }[]>([]);

  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    mutateAsync: addAgreements,
    isLoading: isAddingAgreements,
    error: addAgreementsError,
  } = useAddAgreementsMutation({ isFirm });

  const handleAddAgreements = async () => {
    try {
      if (uploadedAgreements.current.length === 0) {
        setValidationError('Upload a valid PDF file');
        return;
      }

      const newAgreements = await addAgreements({
        agreements: uploadedAgreements.current.map(({ name, size }) => ({
          file: name,
          fileSize: size,
        })),
      });

      onAddAgreements(newAgreements);
    } catch (err) {
      console.error(err);
    }
  };

  const dropZoneOptions: DropzoneOptions = {
    accept: '.pdf',
    multiple: true,
    maxSize: MAX_FILE_SIZE * 1e6,
    onDropAccepted(files) {
      const validAgreements: File[] = [];
      const duplicatedAgreements: { file: File; error: string }[] = [];

      files.forEach(file => {
        if (
          !uploadingAgreements.some(({ name }) => name === file.name) &&
          !agreements.some(({ file: fileName }) => fileName === file.name)
        ) {
          validAgreements.push(file);
        } else {
          duplicatedAgreements.push({ file, error: 'Duplicate file names are not allowed.' });
        }
      });

      setUploadingAgreements(prevUploadingAgreements => [
        ...prevUploadingAgreements,
        ...validAgreements,
      ]);

      setInvalidAgreements(duplicatedAgreements);
    },
  };

  return (
      <Modal isOpen onRequestClose={onClose} contentLabel="Agreements upload">
      <ModalHeader>Upload</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {addAgreementsError && (
          <Alert message={addAgreementsError.message} variant="error" mb="16" />
        )}

        {validationError && (
          <Alert
            message={validationError}
            onRequestClose={() => setValidationError(null)}
            variant="error"
            mb="16"
          />
        )}

        <Dropzone options={dropZoneOptions}>
          {({ fileRejections }) => (
            <Box>
              {uploadingAgreements.map(file => (
                <UploadAgreement
                  key={file.name}
                  isFirm={isFirm}
                  file={file}
                  message="Uploading..."
                  variant="uploading"
                  onUploadSuccess={() => {
                    uploadedAgreements.current.push(file);
                    setValidationError(null);
                  }}
                  mt="32"
                />
              ))}

              {invalidAgreements.map(({ file, error }) => (
                <UploadAgreement
                  key={file.name}
                  isRejected
                  file={file}
                  message={error}
                  progress={100}
                  isFirm={isFirm}
                  mt="32"
                  variant="error"
                />
              ))}

              {fileRejections.map(({ file, errors }) => (
                <UploadAgreement
                  key={file.name}
                  isRejected
                  file={file}
                  message={getMessageFromErrors(errors)}
                  progress={100}
                  isFirm={isFirm}
                  mt="32"
                  variant="error"
                />
              ))}
            </Box>
          )}
        </Dropzone>
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onClose}>
          Cancel
        </Button>

        <Button onClick={handleAddAgreements} isLoading={isAddingAgreements} ml="12">
          OK
        </Button>
      </ModalFooter>
    </Modal>
  );
};
